import React from 'react';
import { Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { acmgCriteria, ACMGCriterion } from '../../data/acmgCriteria';
import { PrintablePage } from './styles';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: '4px 8px',
  fontSize: '0.7rem',
  '&.MuiTableCell-head': {
    backgroundColor: theme.palette.grey[100],
    fontWeight: 'bold',
  },
}));

const StrengthChip = styled(Chip)<{ strength: ACMGCriterion['strength'] }>(
  ({ theme, strength }) => ({
    backgroundColor: {
      'Very Strong': theme.palette.error.main,
      'Strong': theme.palette.error.light,
      'Moderate': theme.palette.warning.main,
      'Supporting': theme.palette.info.main,
    }[strength],
    color: theme.palette.common.white,
    height: '16px',
    '& .MuiChip-label': {
      padding: '0 6px',
      fontSize: '0.65rem',
    },
  })
);

export const ACMGCriteriaSection: React.FC = () => {
  return (
    <PrintablePage>
      <Typography variant="h2" gutterBottom color="primary">
        ACMG Assertion Criteria
      </Typography>
      
      <Box display="flex" gap={2} mb={1} alignItems="center">
        <Typography variant="caption" color="text.secondary" mb={1}>
          The ACMG guidelines provide a framework for evaluating genetic variants.
        </Typography>
        <Box display="flex" gap={1} alignItems="center" alignSelf="baseline">
          <Box display="flex" gap={0.5} alignItems="center">
            <Chip
              label="A"
              size="small"
              sx={{
                height: '16px',
                '& .MuiChip-label': {
                  padding: '0 4px',
                  fontSize: '0.65rem',
                },
              }}
              color="info"
            />
            <Typography variant="caption">Automated</Typography>
          </Box>
          <Box display="flex" gap={0.5} alignItems="center">
            <Chip
              label="M"
              size="small"
              sx={{
                height: '16px',
                '& .MuiChip-label': {
                  padding: '0 4px',
                  fontSize: '0.65rem',
                },
              }}
              color="default"
            />
            <Typography variant="caption">Manual</Typography>
          </Box>
        </Box>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell>Code</StyledTableCell>
              <StyledTableCell>Strength</StyledTableCell>
              <StyledTableCell>Type</StyledTableCell>
              <StyledTableCell>Description</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {['Pathogenic', 'Benign'].map((category) => (
              <React.Fragment key={category}>
                <TableRow>
                  <StyledTableCell 
                    colSpan={4} 
                    sx={{ 
                      fontWeight: 'bold',
                      backgroundColor: (theme) => theme.palette.grey[50],
                    }}
                  >
                    {category} Criteria
                  </StyledTableCell>
                </TableRow>
                {acmgCriteria
                  .filter((criterion) => criterion.category === category)
                  .map((criterion) => (
                    <TableRow key={criterion.code}>
                      <StyledTableCell>{criterion.code}</StyledTableCell>
                      <StyledTableCell>
                        <StrengthChip
                          label={criterion.strength.split(' ')[0]}
                          strength={criterion.strength}
                          size="small"
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        <Chip
                          label={criterion.automated ? 'A' : 'M'}
                          size="small"
                          sx={{
                            height: '16px',
                            '& .MuiChip-label': {
                              padding: '0 4px',
                              fontSize: '0.65rem',
                            },
                          }}
                          color={criterion.automated ? 'info' : 'default'}
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        {criterion.description}
                        {criterion.requirements && (
                          <>
                            <br />
                            <span style={{ fontWeight: 'bold' }}>Requirements: </span>
                            {criterion.requirements.join('; ')}
                          </>
                        )}
                      </StyledTableCell>
                    </TableRow>
                  ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </PrintablePage>
  );
}; 